/*
 * Copyright © 2018-2024, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import moment from "moment";
import { TimeZoneResponse } from "../../../models/time/TimeZoneResponse";
import { AddressRequest } from "../../../models/contact/AddressRequest";
import { LocatedAddressRequest } from "../../../models/contact/LocatedAddressRequest";
import { getGeneralError } from "../../../util/helperFunctions";
import CombinedSelect from "../../ReactHookFormFields/General/Select/CombinedSelect";
import TimeZoneApi from "../../../api/TimeZoneApi";
import { strings } from "../../../common/Strings/Strings";
import AlertBox from "../../AlertBox";
import Button from "../../Button";

interface Props {
  address?: AddressRequest;
  countryName?: string;
  changeAddress(): void;
  isMapCenterToCurrentLocation: boolean;
  setLocalizedAddress(address: LocatedAddressRequest, onError?: (error: any) => void, onFinally?: () => void): void;
}

interface AddressLocationForm {
  timeZone: TimeZoneResponse[] | null;
  verifiedByUser: boolean;
}

const ZoneIdForm: React.FC<Props> = ({ address, setLocalizedAddress }: Props) => {
  const [timeZones, setTimeZones] = useState<TimeZoneResponse[]>([]);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const {
    control,
    setValue,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = useForm<AddressLocationForm>({
    defaultValues: { verifiedByUser: false },
    mode: "onChange",
  });

  useEffect(() => {
    const getTimeZones = async () => {
      try {
        const resp = await TimeZoneApi.getTimeZones();
        setTimeZones(resp.data);
      } catch (err) {
        setError(await getGeneralError(err));
      }
    };

    void getTimeZones();
  }, []);

  useEffect(() => {
    if (!address) return;

    const guessedTimeZone = timeZones.find((t) => t.zoneId.includes(address.city));

    const defaultTimeZone = timeZones.find((t) => t.zoneId.includes(moment.tz.guess()));

    if (guessedTimeZone) {
      setValue("timeZone", [guessedTimeZone]);
    } else if (defaultTimeZone) {
      setValue("timeZone", [defaultTimeZone]);
    }
  }, [address, setValue, timeZones]);

  const onError = async (err: any) => {
    setError(await getGeneralError(err));
  };

  const onFinally = () => {
    setLoading(false);
  };

  const submit = ({ timeZone }: AddressLocationForm) => {
    if (!address) return;

    setLoading(true);
    if (location && timeZone) {
      const request: LocatedAddressRequest = {
        ...address,
        zoneId: timeZone[0].zoneId,
        latitude: 0,
        longitude: 0,
      };
      setLocalizedAddress(request, onError, onFinally);
    }
  };

  return (
    <div className="space-y-6">
      <form className="space-y-6" onSubmit={handleSubmit(submit)}>
        <CombinedSelect
          allowNew={false}
          control={control}
          isTypeahead
          label={strings.timeZone}
          labelKey="zoneId"
          multiple={false}
          name="timeZone"
          options={timeZones}
          placeholder={strings.chooseFromWithDots}
          required
          showRequired
        />
        <AlertBox message={error} />
        <AlertBox hidden={errors.timeZone?.type !== "required"} message={strings.pleaseFillRequiredFields} />
        <Button disabled={isSubmitting || loading} loading={loading} type="submit" variant="primary">
          {strings.saveAndContinue}
        </Button>
      </form>
    </div>
  );
};

export default ZoneIdForm;
